import React, { useState, useEffect } from "react";
import {
  ShoppingCart,
  CreditCard,
  TrendingUp,
  Wallet,
  Clock,
  BarChart3,
  Users,
  CheckCircle,
  XCircle,
  AlertCircle,
} from "lucide-react";
import * as S from "./dashboardStyle";
import { Helper } from "@utils/index";
import { useGeneralSettings } from "@hooks/useGeneralSettings";
import userApi from "@apis/user";

interface CustomerOverviewDashboardProps {
  customerId: number;
}

const CustomerOverviewDashboard: React.FC<CustomerOverviewDashboardProps> = ({
  customerId,
}) => {
  const { settings } = useGeneralSettings();
  const [analyticsData, setAnalyticsData] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchAnalytics = async () => {
      try {
        setLoading(true);
        const res = await (userApi as any).getCustomerAnalytics({
          id: customerId,
        });
        setAnalyticsData(res);
      } catch (error) {
        console.error("Failed to fetch customer analytics", error);
      } finally {
        setLoading(false);
      }
    };
    fetchAnalytics();
  }, [customerId]);

  if (loading) return <div>Loading analytics...</div>;
  if (!analyticsData) return <div>No analytics data available</div>;

  const {
    orderOverview,
    walletBalance,
    referralCount,
    transactionOverview,
    recentTransactions,
  } = analyticsData;

  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case "delivered":
      case "completed":
      case "success":
        return "#10b981";
      case "pending":
      case "order placed":
        return "#f59e0b";
      case "processing":
      case "order processing":
        return "#3b82f6";
      case "cancelled":
      case "failed":
        return "#ef4444";
      default:
        return "#6b7280";
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status.toLowerCase()) {
      case "delivered":
      case "completed":
      case "success":
      case "approved":
        return <CheckCircle size={16} />;
      case "pending":
        return <Clock size={16} />;
      case "processing":
        return <AlertCircle size={16} />;
      case "cancelled":
      case "failed":
        return <XCircle size={16} />;
      default:
        return <AlertCircle size={16} />;
    }
  };

  return (
    <S.DashboardContainer>
      {/* Overview Metrics */}
      <S.MetricsGrid>
        <S.MetricCard>
          <S.MetricIcon bgColor="#6366f1" className="metric-icon">
            <Wallet size={24} />
          </S.MetricIcon>
          <div style={{ marginTop: 16 }}>
            <S.MetricValue>
              {Helper.formatCurrency(walletBalance || 0, settings)}
            </S.MetricValue>
            <S.MetricLabel>Wallet Balance</S.MetricLabel>
          </div>
        </S.MetricCard>

        <S.MetricCard>
          <S.MetricIcon bgColor="#10b981" className="metric-icon">
            <ShoppingCart size={24} />
          </S.MetricIcon>
          <div style={{ marginTop: 16 }}>
            <S.MetricValue>{transactionOverview?.total || 0}</S.MetricValue>
            <S.MetricLabel>Total Orders</S.MetricLabel>
          </div>
        </S.MetricCard>

        <S.MetricCard>
          <S.MetricIcon bgColor="#f59e0b" className="metric-icon">
            <Users size={24} />
          </S.MetricIcon>
          <div style={{ marginTop: 16 }}>
            <S.MetricValue>{referralCount || 0}</S.MetricValue>
            <S.MetricLabel>Referrals</S.MetricLabel>
          </div>
        </S.MetricCard>

        <S.MetricCard>
          <S.MetricIcon bgColor="#3b82f6" className="metric-icon">
            <TrendingUp size={24} />
          </S.MetricIcon>
          <div style={{ marginTop: 16 }}>
            <S.MetricValue>
              {Helper.formatCurrency(
                transactionOverview?.credit || 0,
                settings,
              )}
            </S.MetricValue>
            <S.MetricLabel>Total Spent (approx)</S.MetricLabel>
          </div>
        </S.MetricCard>
      </S.MetricsGrid>

      {/* Financial Breakdown */}
      <S.Section>
        <S.SectionTitle>
          <BarChart3 size={20} />
          Financial Breakdown
        </S.SectionTitle>
        <S.BreakdownGrid>
          <S.BreakdownCard highlight>
            <S.IconContainer color="#ffffff" highlight>
              <TrendingUp size={16} />
            </S.IconContainer>
            <S.BreakdownLabel inverted>Total Credits</S.BreakdownLabel>
            <S.BreakdownValue inverted>
              {Helper.formatCurrency(
                transactionOverview?.credit || 0,
                settings,
              )}
            </S.BreakdownValue>
          </S.BreakdownCard>

          <S.BreakdownCard>
            <S.IconContainer color="#ef4444">
              <Clock size={16} />
            </S.IconContainer>
            <S.BreakdownLabel>Total Debits</S.BreakdownLabel>
            <S.BreakdownValue color="#ef4444">
              {Helper.formatCurrency(transactionOverview?.debit || 0, settings)}
            </S.BreakdownValue>
          </S.BreakdownCard>
        </S.BreakdownGrid>
      </S.Section>

      {/* Order Status Overview */}
      <S.Section>
        <S.SectionTitle>
          <ShoppingCart size={20} />
          Order Status Overview
        </S.SectionTitle>
        <S.StatsGrid>
          {Object.entries(orderOverview).length > 0 ? (
            Object.entries(orderOverview).map(([status, count]: any) => (
              <S.StatItem key={status}>
                <S.StatLabel>{status}</S.StatLabel>
                <S.StatValue color={getStatusColor(status)}>
                  {count}
                </S.StatValue>
              </S.StatItem>
            ))
          ) : (
            <div style={{ padding: "20px", color: "#64748b" }}>
              No order statistics available.
            </div>
          )}
        </S.StatsGrid>
      </S.Section>

      {/* Recent Transactions */}
      <S.Section>
        <S.SectionTitle>
          <CreditCard size={20} />
          Recent Transactions
        </S.SectionTitle>
        <S.TableContainer>
          <S.Table>
            <S.TableHeader>
              <S.TableRow>
                <S.TableHead>Amount</S.TableHead>
                <S.TableHead>Type</S.TableHead>
                <S.TableHead>Purpose</S.TableHead>
                <S.TableHead>Date</S.TableHead>
              </S.TableRow>
            </S.TableHeader>
            <S.TableBody>
              {recentTransactions && recentTransactions.length > 0 ? (
                recentTransactions.map((tr: any) => (
                  <S.TableRow key={tr.id}>
                    <S.TableCell>
                      <S.AmountText positive={tr.type === "credit"}>
                        {Helper.formatCurrency(tr.amount, settings)}
                      </S.AmountText>
                    </S.TableCell>
                    <S.TableCell>
                      <S.StatusBadge
                        color={tr.type === "credit" ? "#10b981" : "#ef4444"}
                      >
                        {getStatusIcon(
                          tr.type === "credit" ? "approved" : "failed",
                        )}
                        {tr.type}
                      </S.StatusBadge>
                    </S.TableCell>
                    <S.TableCell>{tr.purpose || "-"}</S.TableCell>
                    <S.TableCell>
                      {new Date(tr.createdAt).toLocaleDateString()}
                    </S.TableCell>
                  </S.TableRow>
                ))
              ) : (
                <S.TableRow>
                  <S.TableCell colSpan={4} style={{ textAlign: "center" }}>
                    No recent transactions found
                  </S.TableCell>
                </S.TableRow>
              )}
            </S.TableBody>
          </S.Table>
        </S.TableContainer>
      </S.Section>
    </S.DashboardContainer>
  );
};

export default CustomerOverviewDashboard;
